import React from 'react'

function mapI18nToSpeechLang(i18nLang: string) {
  const l = (i18nLang || '').toLowerCase()
  if (l === 'kk') return 'kk-KZ'
  if (l === 'en') return 'en-US'
  return 'ru-RU'
}

function loadVoicesSafe(): SpeechSynthesisVoice[] {
  try {
    return window.speechSynthesis?.getVoices?.() || []
  } catch {
    return []
  }
}

function chooseVoice(voices: SpeechSynthesisVoice[], primaryLang: string) {
  if (!voices?.length) return { voice: null as SpeechSynthesisVoice | null, lang: primaryLang }

  const primary = primaryLang.toLowerCase()
  const base = primary.split('-')[0]

  const pickBest = (arr: SpeechSynthesisVoice[]) =>
    arr.find((v) => v.default) || arr.find((v) => (v as any).localService) || arr[0] || null

  const exact = voices.filter((v) => (v.lang || '').toLowerCase() === primary)
  const byBase = voices.filter((v) => (v.lang || '').toLowerCase().startsWith(base))

  let voice = pickBest(exact) || pickBest(byBase)

  // Если нет казахского голоса — fallback на русский
  if (!voice && base === 'kk') {
    const ru = voices.filter((v) => (v.lang || '').toLowerCase().startsWith('ru'))
    voice = pickBest(ru)
    if (voice) return { voice, lang: 'ru-RU' }
  }

  return { voice: voice || null, lang: voice?.lang || primaryLang }
}

function speakText(
  text: string,
  opts: { lang?: string; voice?: SpeechSynthesisVoice | null; rate?: number; pitch?: number; volume?: number } = {}
) {
  if (!('speechSynthesis' in window)) return
  window.speechSynthesis.cancel()

  const u = new SpeechSynthesisUtterance(text)
  if (opts.lang) u.lang = opts.lang
  if (opts.voice) u.voice = opts.voice

  u.rate = opts.rate ?? 1
  u.pitch = opts.pitch ?? 1
  u.volume = opts.volume ?? 1

  window.speechSynthesis.speak(u)
}

function getTextAtPoint(e: PointerEvent | MouseEvent) {
  const anyDoc = document as any

  const range: Range | null =
    anyDoc.caretRangeFromPoint?.(e.clientX, e.clientY) ||
    (() => {
      const pos = anyDoc.caretPositionFromPoint?.(e.clientX, e.clientY)
      if (!pos) return null
      const r = document.createRange()
      r.setStart(pos.offsetNode, pos.offset)
      r.setEnd(pos.offsetNode, pos.offset)
      return r
    })()

  if (!range) return ''
  const node = range.startContainer
  if (!node) return ''

  if (node.nodeType === Node.TEXT_NODE) return (node.textContent || '').trim()

  const el = (node.nodeType === 1 ? (node as Element) : (node.parentElement as Element | null))?.closest?.(
    'p,span,a,button,label,li,td,th,h1,h2,h3,h4,h5,h6'
  )
  if (!el) return ''
  return ((el as HTMLElement).innerText || el.textContent || '').trim()
}

function getClickableText(e: PointerEvent | MouseEvent, excludeSelector?: string) {
  const target = e.target as Element | null
  if (!target) return ''

  if (excludeSelector && target.closest?.(excludeSelector)) return ''

  const pointText = getTextAtPoint(e)
  if (pointText) return pointText

  const el = target.closest?.('p,span,a,button,label,li,td,th,h1,h2,h3,h4,h5,h6') as HTMLElement | null
  if (!el) return ''
  return (el.innerText || el.textContent || '').trim()
}

export function useA11yTts(
  enabled: boolean,
  {
    i18nLang = 'ru',
    excludeSelector = '.a11y-modal,.a11y-page-card,.a11y-ignore-tts',
    rateByLang = { 'ru-RU': 1, 'en-US': 1, 'kk-KZ': 0.95 }
  }: {
    i18nLang?: string
    excludeSelector?: string
    rateByLang?: Record<string, number>
  } = {}
) {
  const [voices, setVoices] = React.useState<SpeechSynthesisVoice[]>(() => loadVoicesSafe())

  React.useEffect(() => {
    if (!('speechSynthesis' in window)) return
    const load = () => setVoices(loadVoicesSafe())
    load()
    window.speechSynthesis.addEventListener?.('voiceschanged', load)
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', load)
  }, [])

  React.useEffect(() => {
    if (!enabled) {
      window.speechSynthesis?.cancel?.()
      return
    }

    const handler = (e: any) => {
      try {
        const sel = window.getSelection?.()
        if (sel && sel.toString().trim()) return

        const raw = getClickableText(e, excludeSelector)
        const clean = (raw || '').replace(/\s+/g, ' ').trim()
        if (clean.length < 2) return

        const chunk = clean.length > 320 ? clean.slice(0, 320) + '…' : clean

        const primaryLang = mapI18nToSpeechLang(i18nLang)
        const { voice, lang } = chooseVoice(voices, primaryLang)
        const rate = rateByLang[primaryLang] ?? 1

        speakText(chunk, { lang, voice, rate })
      } catch {
        // no-op
      }
    }

    document.addEventListener('pointerup', handler, true)
    document.addEventListener('click', handler, true)

    return () => {
      document.removeEventListener('pointerup', handler, true)
      document.removeEventListener('click', handler, true)
    }
  }, [enabled, i18nLang, excludeSelector, voices, rateByLang])
}
